// Theme state — two daisyUI themes wired in `tailwind.config.js`:
//   "scailabs"      — light (deep teal #197474 on white)
//   "scailabs-dark" — dark  (mint #3BB58E on navy #1a1a2e)
//
// Resolution order: localStorage → prefers-color-scheme → light fallback.
// First-visit users follow their OS preference; once they toggle, the choice
// is stored and persists across visits.
//
// The FOUC-prevention inline script in `index.html` applies the right theme
// before SolidJS bootstraps; this module keeps the signal in sync after that.

import { createSignal } from "solid-js";

export type Theme = "scailabs" | "scailabs-dark";

const STORAGE_KEY = "scaicrm.theme";

function detectInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "scailabs" || stored === "scailabs-dark") return stored;
  } catch {
    // private mode / SSR — fall through to system preference
  }
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "scailabs-dark";
  }
  return "scailabs";
}

const initial = detectInitialTheme();
const [theme, setThemeSignal] = createSignal<Theme>(initial);

if (typeof document !== "undefined") {
  // Honor what the inline FOUC script set, or set it ourselves if it didn't.
  document.documentElement.setAttribute("data-theme", initial);

  // Follow OS changes only when the user hasn't expressed a preference.
  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const t: Theme = e.matches ? "scailabs-dark" : "scailabs";
    setThemeSignal(t);
    document.documentElement.setAttribute("data-theme", t);
  });
}

export const currentTheme = theme;
export const isDark = () => theme() === "scailabs-dark";

export function setTheme(t: Theme): void {
  setThemeSignal(t);
  try {
    localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // ignore — preference simply won't persist
  }
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", t);
  }
}

export function toggleTheme(): void {
  setTheme(isDark() ? "scailabs" : "scailabs-dark");
}
